/**
 * Wrapper for EdgeAgentDetector to match AgentDetector interface
 * This allows the middleware to work with EdgeAgentDetector in Edge Runtime
 *
 * This is a self-contained implementation to avoid import resolution issues
 * Includes proper Ed25519 signature verification for ChatGPT and other agents
 */

type DetectionInput = {
  userAgent?: string;
  ip?: string;
  ipAddress?: string;
  headers?: Record<string, string>;
  url?: string;
  method?: string;
  timestamp?: Date;
};

// Import the proper DetectionResult type from shared package
import type { DetectionResult } from '@kya-os/agentshield-shared';

type EventHandler = (...args: unknown[]) => void;

// Import signature verifier
import {
  verifyAgentSignature,
  hasSignatureHeaders,
  isChatGPTSignature,
} from './signature-verifier';
import {
  loadRulesSync,
  type DetectionRules,
  type UserAgentRule,
  type HeaderRule,
  type IpRangeRule,
} from '@kya-os/agentshield-shared';

// Load centralized rules - this will fail fast if rules cannot be loaded
const rules: DetectionRules = loadRulesSync();

/**
 * Self-contained EdgeAgentDetector implementation with proper signature verification
 */
class EdgeAgentDetector {
  private rules: DetectionRules;

  constructor() {
    this.rules = rules;
  }

  async analyze(input: DetectionInput): Promise<DetectionResult> {
    const reasons: string[] = [];
    let detectedAgent: { type: string; name: string } | undefined;
    let verificationMethod: string | undefined;
    let confidence = 0;

    const headers = input.headers || {};
    const normalizedHeaders: Record<string, string> = {};

    // Normalize header names to lowercase
    for (const [key, value] of Object.entries(headers)) {
      normalizedHeaders[key.toLowerCase()] = value;
    }

    // Check for HTTP Message Signatures with proper cryptographic verification
    if (hasSignatureHeaders(headers)) {
      try {
        // Verify the signature cryptographically
        const signatureResult = await verifyAgentSignature(
          input.method || 'GET',
          input.url || '/',
          headers
        );

        if (signatureResult.isValid) {
          // Valid cryptographic signature - highest confidence (convert 0-1 to 0-100)
          confidence = signatureResult.confidence * 100;
          reasons.push(`verified_signature:${signatureResult.agent?.toLowerCase() || 'unknown'}`);
          if (signatureResult.agent) {
            detectedAgent = {
              type: signatureResult.agent.toLowerCase(),
              name: signatureResult.agent,
            };
          }
          verificationMethod = signatureResult.verificationMethod;

          // Add keyid to reasons for debugging
          if (signatureResult.keyid) {
            reasons.push(`keyid:${signatureResult.keyid}`);
          }
        } else {
          // Invalid signature - suspicious
          console.warn('[EdgeAgentDetector] Signature verification failed:', {
            reason: signatureResult.reason,
            agent: signatureResult.agent,
            hasSignatureAgent: !!headers['signature-agent'] || !!headers['Signature-Agent'],
            signatureAgentValue: headers['signature-agent'] || headers['Signature-Agent'],
          });
          confidence = Math.max(confidence, 30); // Updated to 0-100 scale (was 0.3)
          reasons.push('invalid_signature');
          if (signatureResult.reason) {
            reasons.push(`signature_error:${signatureResult.reason}`);
          }

          // Still check if it claims to be ChatGPT
          if (isChatGPTSignature(headers)) {
            reasons.push('claims_chatgpt');
            detectedAgent = { type: 'chatgpt', name: 'ChatGPT (unverified)' };
          }
        }
      } catch (error) {
        // Error during verification
        console.error('[EdgeAgentDetector] Signature verification error:', error);
        confidence = Math.max(confidence, 20); // Updated to 0-100 scale (was 0.2)
        reasons.push('signature_verification_error');
      }
    }

    // Check User-Agent patterns using centralized rules
    // Sort rules to check more specific patterns first (generic_bot last)
    const userAgent = input.userAgent || input.headers?.['user-agent'] || '';
    if (userAgent) {
      const userAgentEntries = Object.entries(this.rules.rules.userAgents);
      // Sort: put generic_bot, dev_tools, automation_tools last since they have broad patterns
      const genericKeys = ['generic_bot', 'dev_tools', 'automation_tools'];
      const sortedEntries = userAgentEntries.sort((a, b) => {
        const aIsGeneric = genericKeys.includes(a[0]);
        const bIsGeneric = genericKeys.includes(b[0]);
        if (aIsGeneric && !bIsGeneric) return 1;
        if (!aIsGeneric && bIsGeneric) return -1;
        return 0;
      });

      for (const [agentKey, agentRule] of sortedEntries) {
        const rule = agentRule as UserAgentRule;
        const matched = rule.patterns.some((pattern: string) => {
          const regex = new RegExp(pattern, 'i');
          return regex.test(userAgent);
        });

        if (matched) {
          const agentType = this.getAgentType(agentKey);
          const agentName = this.getAgentName(agentKey);

          confidence = Math.max(confidence, rule.confidence * 100); // Convert 0-1 rule confidence to 0-100 scale
          reasons.push(`known_pattern:${agentType}`);
          if (!detectedAgent) {
            detectedAgent = { type: agentType, name: agentName };
            verificationMethod = 'pattern';
          }
          break;
        }
      }
    }

    // Check AI-specific headers using centralized rules
    const suspiciousHeaders = this.rules.rules.headers.suspicious;
    const foundAiHeaders = suspiciousHeaders.filter(
      (headerRule: HeaderRule) => normalizedHeaders[headerRule.name.toLowerCase()]
    );

    if (foundAiHeaders.length > 0) {
      const maxConfidence = Math.max(...foundAiHeaders.map((h: HeaderRule) => h.confidence * 100)); // Convert 0-1 to 0-100
      confidence = Math.max(confidence, maxConfidence);
      reasons.push(`ai_headers:${foundAiHeaders.length}`);
    }

    // Check cloud provider IPs using centralized rules
    const ip = input.ip || input.ipAddress;
    if (ip && !normalizedHeaders['x-forwarded-for'] && !normalizedHeaders['x-real-ip']) {
      // Handle both old (with providers) and new (direct) structures
      const ipRanges =
        'providers' in this.rules.rules.ipRanges
          ? this.rules.rules.ipRanges.providers
          : this.rules.rules.ipRanges;

      for (const [provider, ipRule] of Object.entries(ipRanges)) {
        // Skip non-rule entries
        if (
          !ipRule ||
          typeof ipRule !== 'object' ||
          !('ranges' in ipRule) ||
          !Array.isArray(ipRule.ranges)
        )
          continue;

        const matched = ipRule.ranges.some((range: string) => {
          const prefix = range.split('/')[0];
          const prefixParts = prefix.split('.');
          const ipParts = ip.split('.');

          // Check if IP starts with the same octets
          for (let i = 0; i < Math.min(prefixParts.length - 1, 2); i++) {
            if (prefixParts[i] !== ipParts[i] && prefixParts[i] !== '0') {
              return false;
            }
          }
          return true;
        });

        if (matched) {
          const rule = ipRule as IpRangeRule;
          confidence = Math.max(confidence, rule.confidence * 40); // Convert 0-1 to 0-100 with 0.4 reduction factor
          reasons.push(`cloud_provider:${provider}`);
          break;
        }
      }
    }

    // Boost confidence for combinations (but not if already at 100 from signature)
    if (reasons.length > 2 && confidence < 100) {
      confidence = Math.min(confidence * 1.2, 95); // Cap at 95 to leave room for cryptographic verification
    }

    // Ensure confidence stays within 0-100 range
    confidence = Math.min(Math.max(confidence, 0), 100);

    return {
      isAgent: confidence > 30, // Updated to 0-100 scale (was 0.3)
      confidence,
      detectionClass:
        confidence > 30 && detectedAgent // Updated to 0-100 scale
          ? { type: 'AiAgent' as const, agentType: detectedAgent.name }
          : confidence > 30 // Updated to 0-100 scale
            ? { type: 'Unknown' as const }
            : { type: 'Human' as const },
      signals: [], // Will be populated by enhanced detection engine in future tasks
      ...(detectedAgent && { detectedAgent }),
      reasons,
      ...(verificationMethod && {
        verificationMethod: verificationMethod as
          | 'signature'
          | 'pattern'
          | 'behavioral'
          | 'network'
          | 'mcp_i_handshake'
          | 'none',
      }),
      forgeabilityRisk:
        verificationMethod === 'signature' ? 'low' : confidence > 80 ? 'medium' : 'high', // Updated to 0-100 scale
      timestamp: new Date(),
    };
  }

  /**
   * Get agent type from rule key
   */
  private getAgentType(agentKey: string): string {
    const typeMap: Record<string, string> = {
      openai_gptbot: 'openai',
      anthropic_claude: 'anthropic',
      perplexity_bot: 'perplexity',
      google_ai: 'google',
      microsoft_ai: 'microsoft',
      meta_ai: 'meta',
      cohere_bot: 'cohere',
      huggingface_bot: 'huggingface',
      generic_bot: 'generic',
      dev_tools: 'dev',
      automation_tools: 'automation',
    };
    return typeMap[agentKey] || agentKey;
  }

  /**
   * Get agent name from rule key
   */
  private getAgentName(agentKey: string): string {
    const nameMap: Record<string, string> = {
      openai_gptbot: 'ChatGPT/GPTBot',
      anthropic_claude: 'Claude',
      perplexity_bot: 'Perplexity',
      google_ai: 'Google AI',
      microsoft_ai: 'Microsoft Copilot',
      meta_ai: 'Meta AI',
      cohere_bot: 'Cohere',
      huggingface_bot: 'HuggingFace',
      generic_bot: 'Generic Bot',
      dev_tools: 'Development Tool',
      automation_tools: 'Automation Tool',
    };
    return nameMap[agentKey] || agentKey;
  }
}

/**
 * Wrapper that provides event emitter functionality
 */
export class EdgeAgentDetectorWrapper {
  private detector: EdgeAgentDetector;
  private events: Map<string, EventHandler[]> = new Map();

  constructor(_config?: unknown) {
    // Config is accepted but not used by EdgeAgentDetector
    this.detector = new EdgeAgentDetector();
  }

  async analyze(input: DetectionInput): Promise<DetectionResult> {
    const result = await this.detector.analyze(input);

    // Emit events if there are listeners
    if (result.isAgent && this.events.has('agent.detected')) {
      const handlers = this.events.get('agent.detected') || [];
      handlers.forEach((handler) => handler(result, input));
    }

    return result;
  }

  on(event: string, handler: EventHandler): void {
    if (!this.events.has(event)) {
      this.events.set(event, []);
    }
    this.events.get(event)!.push(handler);
  }

  emit(event: string, ...args: unknown[]): void {
    const handlers = this.events.get(event) || [];
    handlers.forEach((handler) => handler(...args));
  }

  async init(): Promise<void> {
    // EdgeAgentDetector doesn't need initialization
    return;
  }
}
